import React, { useState, useEffect } from 'react';
import { XIcon, CheckCircle2, Sparkles } from 'lucide-react';
import { usePremium } from './PremiumProvider';
import { PRICING_TIERS } from './constants';
import { lemonSqueezyService } from '../../services/lemonSqueezyService';
import { toast } from 'react-hot-toast';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';

const PremiumUpgradeModal = ({ isOpen, onClose, source = 'unknown' }) => {
  const { userProfile } = usePremium();
  const [selectedTier, setSelectedTier] = useState('6 months');
  // eslint-disable-next-line no-unused-vars
  const [userCurrency, setUserCurrency] = useState('USD');
  // eslint-disable-next-line no-unused-vars
  const [exchangeRate, setExchangeRate] = useState(1);

  useEffect(() => {
    if (!isOpen) return;
    captureConnectionsEvent('upgrade_modal_opened', {
      source: source || 'unknown',
    });
  }, [isOpen, source]);

  useEffect(() => {
    setUserCurrency('USD');
    setExchangeRate(1);
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const handleChoosePlan = async () => {
    try {
      const selectedPlan = PRICING_TIERS.find(tier => tier.period === selectedTier);
      
      if (!selectedPlan) {
        console.error('❌ No plan selected');
        return;
      }

      if (!selectedPlan.productId) {
        throw new Error('Product ID is not defined for the selected plan');
      }

      captureConnectionsEvent('checkout_started', {
        source: source || 'unknown',
        tier: selectedPlan.period,
      });

      const checkout = await lemonSqueezyService.createCheckout(
        selectedPlan.productId,
        userProfile.id
      );

      // Check if we have a URL before redirecting
      if (checkout?.attributes?.url) {
        window.location.href = checkout.attributes.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      toast.error('Failed to start checkout process. Please try again.');
    }
  };

  // Helper to format period text
  const formatPeriod = (period) => {
    return period.replace(/(^\w|\s\w)/g, letter => letter.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-start justify-center">
        <div className="bg-white rounded-xl w-full max-w-xl mx-auto shadow-xl border-2 border-neutral-900 relative">
          <div className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 sm:mb-5">
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900">Upgrade to Premium</h2>
              <button
                onClick={onClose}
                className="text-neutral-500 hover:text-neutral-700 transition-colors"
                aria-label="Close modal"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Premium Features */}
            <div className="space-y-4 mb-5">
              <p className="text-sm sm:text-base font-semibold text-neutral-800">
                Upgrade today and unlock:
              </p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Weak spots filter</span>
                    <span className="text-neutral-600 text-sm">
                      {' '}– Focus on the baskets you are weakest on, so you stop re-reading what you already know.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Full premade workspaces</span>
                    <span className="text-neutral-600 text-sm">
                      {' '}– Unlock every basket and item in premade workspaces, including the bigger baskets.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Duplicate workspaces</span>
                    <span className="text-neutral-600 text-sm">
                      {' '}– Copy a workspace to start a fresh round of revision.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-2 pt-2 border-t border-neutral-200">
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                    <span className="text-amber-500 font-bold text-sm">PLUS</span>
                    <Sparkles className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <span className="font-semibold">Exclusive Early Access</span>
                    <span className="text-neutral-600 text-sm"> – Be the first to try </span>
                    <span className="font-semibold">Weakest Links, Quiz Mode, and Exam plan</span>
                    <div className="mt-0.5 text-xs text-amber-600 bg-amber-50 py-0.5 px-1.5 rounded-md inline-block">
                      Coming Soon
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* Pricing Tiers */}
            <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-3">
              {PRICING_TIERS.map((tier) => (
                <div 
                  key={tier.period}
                  onClick={() => setSelectedTier(tier.period)}
                  className={`relative border-2 rounded-xl p-3 sm:p-4 transition-all cursor-pointer
                    ${selectedTier === tier.period 
                      ? 'border-cranberry-600 bg-cranberry-50/50' 
                      : 'border-neutral-200 hover:border-neutral-400'}`}
                >
                  <div className="text-center">
                    {tier.badge && (
                      <div className="mb-1.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                          ${tier.period === '6 months' 
                            ? 'bg-cranberry-100 text-cranberry-700' 
                            : 'bg-emerald-100 text-emerald-700'}`}>
                          {tier.badge}
                        </span>
                      </div>
                    )}
                    <h3 className="font-semibold text-base capitalize mb-1">{tier.label}</h3>
                    <div className="text-lg sm:text-xl font-bold text-cranberry-700 mb-0.5">
                      {formatPrice(tier.price)}
                    </div>
                    <div className="text-xs sm:text-sm text-neutral-600 mb-1.5">
                      {formatPrice(tier.perMonth)}/month
                    </div>
                    {tier.savings && (
                      <div className="text-xs font-medium text-emerald-600 bg-emerald-50 py-0.5 px-1.5 rounded-full inline-block">
                        Save {tier.savings}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendations */}
            <div className="mb-4 p-2.5 sm:p-3 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="text-xs font-medium text-blue-700 mb-1">Recommendation</h4>
              {PRICING_TIERS.find(tier => tier.period === selectedTier)?.recommendation && (
                <p className="text-xs text-blue-600 leading-relaxed">
                  {PRICING_TIERS.find(tier => tier.period === selectedTier)?.recommendation}
                </p>
              )}
            </div>

            {/* Action Button */}
            <button
              onClick={handleChoosePlan}
              className="w-full py-2.5 px-4 bg-cranberry-700 text-white rounded-xl font-medium
                       hover:bg-cranberry-600 transition-colors duration-200
                       border-2 border-neutral-900 mb-3
                       text-sm sm:text-base"
            >
              Choose {formatPeriod(
                PRICING_TIERS.find((tier) => tier.period === selectedTier)?.label
                  || selectedTier
              )} Plan
            </button>

            {/* Disclaimer */}
            <p className="text-xs text-neutral-500 text-center px-2">
              * All prices are in USD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;
